"use client";

import { useOnboarding } from "@/lib/use-onboarding";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/**
 * Indicador de progreso de onboarding en el sidebar.
 * Muestra 3 dots que se llenan conforme el usuario completa hitos:
 *  - Workspace creado (espacio)
 *  - Primer documento
 *  - Primer link
 *
 * Click en el componente invoca `reset()` (útil para QA / dev).
 */
export function OnboardingProgress({ className }: { className?: string }) {
  const state = useOnboarding();
  const { t } = useI18n();

  if (state.dismissed) return null;

  const stepKeys: Array<{ key: string; done: boolean }> = [
    { key: "onboarding.step.workspace", done: state.hasProjects },
    { key: "onboarding.step.document", done: state.hasDocuments },
    { key: "onboarding.step.linking", done: state.hasDocuments },
  ];

  const completed = stepKeys.filter((s) => s.done).length;

  return (
    <div
      className={cn("px-3 py-2 border-t border-border/20", className)}
      title={`${completed} / ${stepKeys.length}`}
    >
      <div className="flex items-center gap-1.5">
        {stepKeys.map((s, i) => (
          <div
            key={i}
            className={cn(
              "h-1 flex-1 rounded-full transition-colors",
              s.done ? "bg-primary" : "bg-secondary",
            )}
            aria-label={`${t(s.key)}: ${s.done ? "ok" : "pending"}`}
          />
        ))}
      </div>
      <p className="text-[9px] text-muted-foreground/60 mt-1.5 font-semibold uppercase tracking-wider">
        {completed}/{stepKeys.length} {t("onboarding.milestones")}
      </p>
    </div>
  );
}
